export const CURRENT_YEAR = new Date().getFullYear();
export const DEFAULT_FILTERS = {
  filterCounties: ["all"],
  filterPropertyTypes: ["all"],
  filterAgents: ["all"],
  startYear: 2023,
  endYear: CURRENT_YEAR,
  minBeds: 0,
  maxBeds: 100,
  minPrice: 0,
  maxPrice: 1000000000,
  dataOption: "PPRPrice",
};
export const DATASETS = {
  PPRPrice: {
    label: "Sale prices",
    path: "ppr_price",
    detail: "property",
    price: "Sale price",
  },
  matchedWithPPR: {
    label: "Asking vs sale price",
    path: "undervalued",
    detail: "property",
    price: "Price",
  },
  allHistoricalListings: {
    label: "Historical listings",
    path: "listings",
    detail: "listing",
    price: "Asking price",
  },
  rentals: {
    label: "Rentals",
    path: "rentals",
    detail: "rental",
    price: "Advertised rent",
  },
  shares: {
    label: "House shares",
    path: "shares",
    detail: "share",
    price: "Advertised rent",
  },
};
export function present(value) {
  return (
    value !== null &&
    value !== undefined &&
    String(value).trim() !== "" &&
    !["null", "undefined"].includes(String(value).trim().toLowerCase())
  );
}
export function numeric(value) {
  return present(value) && Number.isFinite(Number(value))
    ? Number(value)
    : null;
}
export function money(value) {
  const number = numeric(value);
  return number === null
    ? "Price unavailable"
    : new Intl.NumberFormat("en-IE", {
        style: "currency",
        currency: "EUR",
        maximumFractionDigits: 0,
      }).format(number);
}
export function dateLabel(value) {
  if (!present(value)) return null;
  const date = new Date(String(value).slice(0, 10));
  return Number.isNaN(date.getTime())
    ? null
    : new Intl.DateTimeFormat("en-IE", {
        day: "numeric",
        month: "short",
        year: "numeric",
        timeZone: "UTC",
      }).format(date);
}
export function priceDifference(asking, sold) {
  const a = numeric(asking),
    s = numeric(sold);
  return a !== null && a > 0 && s !== null && s > 0
    ? ((s - a) / a) * 100
    : null;
}
export function validateFilters(filters) {
  for (const [min, max, label, lower, upper] of [
    ["startYear", "endYear", "Years", 2010, CURRENT_YEAR],
    ["minBeds", "maxBeds", "Bedrooms", 0, 100],
    ["minPrice", "maxPrice", "Prices", 0, 1000000000],
  ]) {
    const a = numeric(filters[min]),
      b = numeric(filters[max]);
    if (
      a === null ||
      b === null ||
      !Number.isInteger(a) ||
      !Number.isInteger(b) ||
      a < lower ||
      b > upper ||
      a > upper ||
      b < lower
    )
      return `${label} must be whole numbers between ${lower.toLocaleString(
        "en-IE"
      )} and ${upper.toLocaleString("en-IE")}.`;
    if (a > b) return `${label}: the minimum must not exceed the maximum.`;
  }
  return "";
}
export function parseSelection(value, options) {
  let parsed = value;
  for (let i = 0; i < 3 && typeof parsed === "string"; i++) {
    try {
      parsed = JSON.parse(parsed);
    } catch {
      parsed = parsed.split(",");
      break;
    }
  }
  const valid = Array.isArray(parsed)
    ? parsed.filter((item) => options.some((option) => option.value === item))
    : [];
  return valid.length && !valid.includes("all") ? valid : ["all"];
}
export function queryForFilters(filters) {
  return new URLSearchParams({
    ...filters,
    minDate: filters.startYear,
    maxDate: filters.endYear,
  });
}
export const API = "https://e4expolexk.execute-api.eu-west-1.amazonaws.com";
export async function fetchJSON(url, signal) {
  const controller = new AbortController();
  const cancel = () => controller.abort();
  if (signal?.aborted) controller.abort();
  signal?.addEventListener("abort", cancel);
  const timer = setTimeout(cancel, 20000);
  try {
    const response = await fetch(url, { signal: controller.signal });
    if (!response.ok) throw new Error(`Request failed (${response.status})`);
    return await response.json();
  } finally {
    clearTimeout(timer);
    signal?.removeEventListener("abort", cancel);
  }
}
export function validFeatures(data) {
  if (!data || !Array.isArray(data.features))
    throw new Error("Invalid map response");
  return data.features.filter(
    (f) =>
      f?.geometry?.type === "Point" &&
      f.properties &&
      Array.isArray(f.geometry.coordinates) &&
      f.geometry.coordinates.length >= 2 &&
      f.geometry.coordinates
        .slice(0, 2)
        .every((n) => typeof n === "number" && Number.isFinite(n)) &&
      Math.abs(f.geometry.coordinates[0]) <= 180 &&
      Math.abs(f.geometry.coordinates[1]) <= 90
  );
}
export function addressFor(data) {
  return (
    [data.clean_address, data.original_address, data.address].find(present) ||
    "Address available in details"
  );
}
export function priceColor(value, mode) {
  const number = numeric(value);
  if (number === null) return "#87929f";
  const [min, max] =
    mode === "rentals"
      ? [800, 4000]
      : mode === "shares"
      ? [200, 1500]
      : [200000, 850000];
  const fraction = Math.max(0, Math.min(1, (number - min) / (max - min)));
  return `rgb(${Math.round(162 - fraction * 133)}, ${Math.round(
    210 - fraction * 140
  )}, ${Math.round(233 - fraction * 96)})`;
}
export function comparisonLabel(value) {
  const n = numeric(value);
  return n === null
    ? "Comparison unavailable"
    : n === 0
    ? "Sold at asking price"
    : `${Math.abs(n).toFixed(1)}% ${n > 0 ? "above" : "below"} asking`;
}
export function comparisonColor(value) {
  const n = numeric(value);
  return n === null
    ? "#87929f"
    : n > 0
    ? "#2669a5"
    : n < 0
    ? "#b96923"
    : "#6555a1";
}

// Separate overlapping records at street-level zoom, retaining original
// coordinates for the map's connecting lines.
export function spreadPoints(points, zoom, spacing = 26) {
  if (zoom < 16) return points.map(({ x, y }) => ({ x, y, displaced: false }));
  const occupied = new Map();
  const cellKey = (x, y) => `${x}:${y}`;
  const available = (candidate) => {
    const cx = Math.floor(candidate.x / spacing),
      cy = Math.floor(candidate.y / spacing);
    for (let dx = -1; dx <= 1; dx++) {
      for (let dy = -1; dy <= 1; dy++) {
        for (const point of occupied.get(cellKey(cx + dx, cy + dy)) || []) {
          if (
            Math.hypot(point.x - candidate.x, point.y - candidate.y) <
            spacing - 0.01
          )
            return false;
        }
      }
    }
    return true;
  };
  return points.map((point) => {
    let candidate = { x: point.x, y: point.y };
    for (let ring = 1; !available(candidate); ring++) {
      const radius = ring * spacing;
      const count = Math.ceil(2 * Math.PI * ring);
      for (let i = 0; i < count; i++) {
        const angle = (2 * Math.PI * i) / count;
        candidate = {
          x: point.x + Math.cos(angle) * radius,
          y: point.y + Math.sin(angle) * radius,
        };
        if (available(candidate)) break;
      }
    }
    const key = cellKey(
      Math.floor(candidate.x / spacing),
      Math.floor(candidate.y / spacing)
    );
    if (!occupied.has(key)) occupied.set(key, []);
    occupied.get(key).push(candidate);
    return {
      ...candidate,
      displaced: candidate.x !== point.x || candidate.y !== point.y,
    };
  });
}
